/**
 * 2026-10-01 — Formulaire de personnalisation GLS (remplace l'application « Eazy Product Options »).
 *
 * La fiche de personnalisation vient du métachamp produit `gls.customization` (métaobjets
 * `gls_customization`, `gls_customization_field`, `gls_customization_label`), sérialisée en JSON par
 * `snippets/product-customizer.liquid`. Ce script dessine les champs, les valide, envoie les photos
 * et pose des champs cachés `properties[<clé>]` que `product-form.js` envoie au panier.
 *
 * 🔴 Trois règles payées par des commandes perdues chez Vunika (juin-septembre 2026) :
 * - la CLÉ de commande reste en français (« Texte 2 », « Numéro ») quelle que soit la langue du
 *   visiteur : l'atelier lit ces clés. Seul le libellé affiché est traduit ;
 * - une photo part au panier sous forme de LIEN vers les fichiers Shopify, jamais sous forme de
 *   champ fichier (sa valeur serait « C:\fakepath\… ») ;
 * - un champ masqué ou vide n'est jamais envoyé, et l'ajout au panier est refusé tant qu'un champ
 *   obligatoire est vide ou qu'une photo est en cours d'envoi (événement `product-form:validate`).
 *
 * Les champs visibles n'ont PAS d'attribut `name` : seuls les champs cachés écrits ici partent au
 * panier, ce qui garde une seule source de vérité.
 *
 * 2026-10-07 — Un champ peut se répéter selon une OPTION du produit (`repeat_by` = « option:<nom> »),
 * pas seulement selon un autre champ : le collier à initiales se vend 1 à 7 initiales à des prix
 * différents, et la liste « Choix » apparaît autant de fois que la variante choisie le dit. Le
 * nombre est relu à chaque `variant:updated`.
 */

const KINDS = {
  Texte: 'text',
  Boutons: 'buttons',
  'Liste déroulante': 'dropdown',
  'Case à cocher': 'checkbox',
  Photo: 'photo',
};
const CHARSETS = { Tous: 'any', Lettres: 'letters', 'Lettres et chiffres': 'letters_digits' };
const CHECKBOX_VALUE = 'Oui';
const TEXTAREA_FROM = 60;

// Accents, espace, trait d'union et apostrophe sont toujours permis (Marie-Ève, N'Golo, 12 bis).
const CHARSET_PATTERNS = {
  letters: /^[\p{L}\p{M}\s'’-]*$/u,
  letters_digits: /^[\p{L}\p{M}\p{N}\s'’-]*$/u,
};

// Une photo est TOUJOURS ré-encodée en JPEG avant l'envoi : cela retire les données EXIF (position GPS
// comprise) et ramène les photos de téléphone (12-50 Mo) à quelques mégaoctets.
const MAX_PHOTO_SIDE = 4000;
const JPEG_QUALITY = 0.85;
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const POLL_ATTEMPTS = 20;

// Repli en français si un libellé `ui-*` manque dans la boutique : un message est toujours affiché.
// Mêmes textes que le vocabulaire GLS (app/services/customization_vocabulary.json), qui crée ces
// libellés et leurs traductions ; les clés sont les poignées sans le préfixe « ui- ».
const DEFAULT_UI = {
  required: 'Ce champ est obligatoire',
  'too-long': '{max} caractères maximum',
  'letters-only': 'Lettres uniquement',
  'letters-digits': 'Lettres et chiffres uniquement',
  'choose-photo': 'Choisir une photo',
  'change-photo': 'Changer de photo',
  uploading: 'Envoi de la photo…',
  'upload-failed': "L'envoi de la photo a échoué",
  retry: 'Réessayer',
  'unsupported-format': 'Format non pris en charge. Choisissez une photo JPEG ou PNG.',
  'too-large': 'Cette photo est trop volumineuse',
  select: 'Choisissez une option',
  counter: '{count}/{max} caractères',
  optional: 'facultatif',
};

let uid = 0;

/** « 3 prénoms » → 3 ; une valeur sans nombre en tête ne répète rien. */
function leadingInt(value) {
  const match = /^\s*(\d+)/.exec(value || '');
  return match ? parseInt(match[1], 10) : 0;
}

/** Clés de commande d'un champ répété : « Texte », « Texte 2 », … (convention héritée de Mandolly). */
function copyKeys(key, count) {
  return Array.from({ length: count }, (_, index) => (index === 0 ? key : `${key} ${index + 1}`));
}

function format(template, values) {
  return String(template).replace(/\{(\w+)\}/g, (match, name) => (name in values ? values[name] : match));
}

function sameText(a, b) {
  return String(a ?? '').trim().toLocaleLowerCase() === String(b ?? '').trim().toLocaleLowerCase();
}

function normalizeField(raw) {
  const kind = KINDS[raw.kind] || raw.kind;
  const choices = Array.isArray(raw.choices) ? raw.choices.filter((choice) => String(choice ?? '').trim() !== '') : [];
  const titles = Array.isArray(raw.choice_titles) ? raw.choice_titles : [];
  return {
    key: String(raw.key || '').trim(),
    title: String(raw.title || '').trim() || String(raw.key || '').trim(),
    kind,
    // Booléen non renseigné dans l'admin = champ obligatoire. L'API d'administration renvoie parfois
    // la chaîne « false ».
    optional: raw.optional === true || raw.optional === 'true',
    maxChars: Number.isInteger(raw.max_chars) && raw.max_chars > 0 ? raw.max_chars : null,
    charset: CHARSETS[raw.charset] || raw.charset || 'any',
    choices: choices.map((value, index) => ({ value, title: String(titles[index] || '').trim() || value })),
    showIf: String(raw.show_if || '').trim() || null,
    showIfValues: Array.isArray(raw.show_if_values) ? raw.show_if_values : [],
    repeatBy: String(raw.repeat_by || '').trim() || null,
  };
}

async function decodeImage(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
      // Certains navigateurs refusent l'option : on retente sans, puis par une balise image.
      try {
        return await createImageBitmap(file);
      } catch (ignored) {
        /* repli ci-dessous */
      }
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function toJpeg(file) {
  const source = await decodeImage(file);
  const width = source.width || source.naturalWidth;
  const height = source.height || source.naturalHeight;
  if (!width || !height) throw new Error('decode');
  const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  // Fond blanc : un PNG transparent ne devient pas noir en JPEG.
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  source.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) throw new Error('encode');
  return blob;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class ProductCustomizer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    const configElement = this.querySelector('script[data-customizer-config]');
    if (!configElement) return;
    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('product-customizer: configuration illisible', error);
      return;
    }
    this.initialized = true;

    this.handle = config.handle || '';
    this.ui = { ...DEFAULT_UI };
    for (const [name, text] of Object.entries(config.ui || {})) {
      if (typeof text === 'string' && text.trim() !== '') this.ui[name] = text;
    }
    this.proxy = (this.dataset.proxy || '/apps/gls').replace(/\/+$/, '');
    this.productId = String(config.product_id ?? '');
    this.optionNames = Array.isArray(config.options) ? config.options : [];
    this.variantOptions = Array.isArray(config.variant_options) ? config.variant_options : [];
    this.fields = (config.fields || []).map(normalizeField).filter((field) => field.key && field.kind);
    this.byKey = new Map(this.fields.map((field) => [field.key.toLocaleLowerCase(), field]));

    /** Valeur saisie par clé de commande concrète (copies comprises). */
    this.values = new Map();
    /** État d'envoi des photos par clé concrète : { status, url, preview, file, message }. */
    this.photos = new Map();
    /** Clés concrètes déjà quittées par le client : leurs erreurs s'affichent sans attendre l'ajout. */
    this.touched = new Set();

    this.list = this.querySelector('[data-customizer-fields]');
    if (!this.list) {
      this.list = document.createElement('div');
      this.list.dataset.customizerFields = '';
      this.appendChild(this.list);
    }
    this.hiddenInputs = document.createElement('div');
    this.hiddenInputs.hidden = true;
    this.appendChild(this.hiddenInputs);

    this.productForm = this.closest('product-form');
    this.onValidate = this.onValidate.bind(this);
    this.productForm?.addEventListener('product-form:validate', this.onValidate);
    this.onVariant = this.onVariant.bind(this);
    document.addEventListener('variant:updated', this.onVariant);

    this.render();
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener('product-form:validate', this.onValidate);
    document.removeEventListener('variant:updated', this.onVariant);
  }

  /** La variante a changé : un champ répété selon une option change peut-être de nombre de copies. */
  onVariant(event) {
    const detail = event.detail || {};
    const productId = String(detail.data?.productId ?? '');
    if (productId && this.productId && productId !== this.productId) return;
    const variant = detail.resource;
    if (!variant) return;
    const options = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
    const before = this.visibleEntries().map((entry) => entry.key).join('\u0000');
    this.variantOptions = options;
    const after = this.visibleEntries().map((entry) => entry.key).join('\u0000');
    if (before !== after) this.render();
  }

  /** Le nombre en tête de la valeur choisie pour l'option `name` (« 3 initiales » → 3), au moins 1. */
  optionCount(name) {
    const index = this.optionNames.findIndex((option) => sameText(option, name));
    // L'option a été renommée : la première valeur choisie qui commence par un nombre.
    const value = index >= 0
      ? this.variantOptions[index]
      : this.variantOptions.find((candidate) => leadingInt(candidate) > 0);
    return Math.max(1, leadingInt(value));
  }

  // --- modèle ---------------------------------------------------------------

  fieldFor(key) {
    return key ? this.byKey.get(key.toLocaleLowerCase()) : undefined;
  }

  /** Le champ s'affiche-t-il, compte tenu de sa condition (et de celle du champ dont elle dépend) ? */
  isShown(field, depth = 0) {
    if (!field.showIf) return true;
    const target = this.fieldFor(field.showIf);
    if (!target || depth > 10 || !this.isShown(target, depth + 1)) return false;
    const value = this.values.get(target.key) || '';
    return field.showIfValues.some((expected) => sameText(expected, value));
  }

  /** Clés concrètes d'un champ affiché : une seule, ou une par copie demandée. */
  concreteKeys(field) {
    if (!field.repeatBy) return [field.key];
    const option = /^option:(.+)$/i.exec(field.repeatBy);
    if (option) return copyKeys(field.key, this.optionCount(option[1].trim()));
    const counter = this.fieldFor(field.repeatBy);
    if (!counter || !this.isShown(counter)) return [];
    return copyKeys(field.key, leadingInt(this.values.get(counter.key)));
  }

  /** [{ field, key, index, count }] — ce qui est à l'écran, dans l'ordre. */
  visibleEntries() {
    const entries = [];
    for (const field of this.fields) {
      if (!this.isShown(field)) continue;
      const keys = this.concreteKeys(field);
      keys.forEach((key, index) => entries.push({ field, key, index, count: keys.length }));
    }
    return entries;
  }

  valueOf(entry) {
    if (entry.field.kind === 'photo') {
      const photo = this.photos.get(entry.key);
      return photo?.status === 'ready' ? photo.url : '';
    }
    return (this.values.get(entry.key) || '').trim();
  }

  errorFor(entry) {
    const { field, key } = entry;
    if (field.kind === 'photo') {
      const photo = this.photos.get(key);
      if (photo?.status === 'uploading' || photo?.status === 'preparing') return this.ui.uploading;
      if (photo?.status === 'failed') return photo.message || this.ui['upload-failed'];
      if (!field.optional && photo?.status !== 'ready') return this.ui.required;
      return null;
    }
    const value = this.valueOf(entry);
    if (value === '') return field.optional ? null : this.ui.required;
    if (field.kind === 'text') {
      if (field.maxChars && [...value].length > field.maxChars) return format(this.ui['too-long'], { max: field.maxChars });
      const pattern = CHARSET_PATTERNS[field.charset];
      if (pattern && !pattern.test(value)) {
        return field.charset === 'letters' ? this.ui['letters-only'] : this.ui['letters-digits'];
      }
    }
    return null;
  }

  isBusy() {
    return [...this.photos.values()].some((photo) => photo.status === 'uploading' || photo.status === 'preparing');
  }

  // --- rendu ----------------------------------------------------------------

  /** Redessine tout en gardant les valeurs et le champ actif : appelé quand la structure change. */
  render() {
    const active = document.activeElement;
    const activeKey = this.contains(active) ? active?.closest('[data-key]')?.dataset.key : null;

    this.list.replaceChildren(...this.visibleEntries().map((entry) => this.renderEntry(entry)));
    this.syncHiddenInputs();

    if (activeKey) {
      this.list.querySelector(`[data-key="${CSS.escape(activeKey)}"] [data-control]`)?.focus({ preventScroll: true });
    }
  }

  titleOf(entry) {
    return entry.count > 1 ? `${entry.field.title} ${entry.index + 1}` : entry.field.title;
  }

  renderEntry(entry) {
    const { field, key } = entry;
    const wrapper = document.createElement('div');
    wrapper.className = `product-customizer__field product-customizer__field--${field.kind}`;
    wrapper.dataset.key = key;
    const id = `product-customizer-${++uid}`;
    const messageId = `${id}-message`;

    const title = document.createElement(field.kind === 'buttons' ? 'p' : 'label');
    title.className = 'product-customizer__title variant_label';
    title.id = `${id}-title`;
    if (field.kind !== 'buttons') title.htmlFor = id;
    title.textContent = this.titleOf(entry);
    // La plupart des champs sont obligatoires : on signale les autres, pas ceux-là.
    if (field.optional) {
      const note = document.createElement('span');
      note.className = 'product-customizer__optional';
      note.textContent = ` (${this.ui.optional})`;
      title.appendChild(note);
    }
    if (field.kind !== 'checkbox') wrapper.appendChild(title);

    let control;
    if (field.kind === 'text') control = this.renderText(entry, id);
    else if (field.kind === 'buttons') control = this.renderButtons(entry, id, title.id);
    else if (field.kind === 'dropdown') control = this.renderDropdown(entry, id);
    else if (field.kind === 'checkbox') control = this.renderCheckbox(entry, id, title);
    else if (field.kind === 'photo') control = this.renderPhoto(entry, id);
    if (control) wrapper.appendChild(control);

    const message = document.createElement('p');
    message.className = 'product-customizer__message';
    message.id = messageId;
    message.setAttribute('role', 'alert');
    wrapper.appendChild(message);
    wrapper.querySelectorAll('[data-control]').forEach((element) => element.setAttribute('aria-describedby', messageId));

    if (this.touched.has(key)) this.showError(wrapper, this.errorFor(entry));
    return wrapper;
  }

  renderText(entry, id) {
    const { field, key } = entry;
    const box = document.createElement('div');
    box.className = 'product-customizer__text';
    const multiline = field.maxChars != null && field.maxChars > TEXTAREA_FROM;
    const input = document.createElement(multiline ? 'textarea' : 'input');
    if (!multiline) input.type = 'text';
    else input.rows = 3;
    input.id = id;
    input.className = 'product-customizer__input';
    input.dataset.control = '';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.value = this.values.get(key) || '';
    if (field.maxChars) input.maxLength = field.maxChars;
    if (!field.optional) input.setAttribute('aria-required', 'true');
    box.appendChild(input);

    let counter = null;
    if (field.maxChars) {
      counter = document.createElement('span');
      counter.className = 'product-customizer__counter';
      counter.setAttribute('aria-hidden', 'true');
      box.appendChild(counter);
    }
    const updateCounter = () => {
      if (counter) counter.textContent = format(this.ui.counter, { count: [...input.value].length, max: field.maxChars });
    };
    updateCounter();

    input.addEventListener('input', () => {
      this.values.set(key, input.value);
      updateCounter();
      this.syncHiddenInputs();
      if (this.touched.has(key)) this.showError(input.closest('[data-key]'), this.errorFor(entry));
    });
    input.addEventListener('blur', () => {
      this.touched.add(key);
      this.showError(input.closest('[data-key]'), this.errorFor(entry));
    });
    if (!multiline) {
      // Entrée passe au champ suivant ; sur le dernier, elle ajoute au panier.
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        this.focusNext(input);
      });
    }
    return box;
  }

  renderButtons(entry, id, titleId) {
    const { field, key } = entry;
    const group = document.createElement('div');
    group.className = 'product-customizer__choices';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-labelledby', titleId);
    const current = this.values.get(key) || '';
    field.choices.forEach((choice, index) => {
      const item = document.createElement('div');
      item.className = 'product-customizer__choice';
      const input = document.createElement('input');
      input.type = 'radio';
      // Nom propre au formulaire de personnalisation : jamais `properties[...]`.
      input.name = `${id}-choice`;
      input.id = `${id}-${index}`;
      input.value = choice.value;
      input.checked = sameText(choice.value, current);
      input.dataset.control = '';
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.touched.add(key);
        this.setValue(key, choice.value);
      });
      const text = document.createElement('label');
      text.htmlFor = input.id;
      text.textContent = choice.title;
      item.append(input, text);
      group.appendChild(item);
    });
    return group;
  }

  renderDropdown(entry, id) {
    const { field, key } = entry;
    const wrapper = document.createElement('div');
    wrapper.className = 'select-wrapper select-wrapper--full-width';
    const select = document.createElement('select');
    select.id = id;
    select.className = 'select select--full-width';
    select.dataset.control = '';
    if (!field.optional) select.setAttribute('aria-required', 'true');
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.ui.select;
    select.appendChild(placeholder);
    const current = this.values.get(key) || '';
    for (const choice of field.choices) {
      const option = document.createElement('option');
      option.value = choice.value;
      option.textContent = choice.title;
      option.selected = sameText(choice.value, current);
      select.appendChild(option);
    }
    select.addEventListener('change', () => {
      this.touched.add(key);
      this.setValue(key, select.value);
    });
    wrapper.appendChild(select);
    return wrapper;
  }

  renderCheckbox(entry, id, title) {
    const { key } = entry;
    const row = document.createElement('div');
    row.className = 'product-customizer__checkbox';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.dataset.control = '';
    input.checked = this.values.get(key) === CHECKBOX_VALUE;
    input.addEventListener('change', () => {
      this.touched.add(key);
      this.setValue(key, input.checked ? CHECKBOX_VALUE : '');
    });
    row.append(input, title);
    return row;
  }

  renderPhoto(entry, id) {
    const { key } = entry;
    const box = document.createElement('div');
    box.className = 'product-customizer__photo';
    const photo = this.photos.get(key);

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.id = id;
    input.className = 'product-customizer__file';
    input.dataset.control = '';
    // Pas de `name` : le champ fichier ne part jamais au panier, seul le lien de la photo envoyée part.
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.value = '';
      if (file) this.uploadPhoto(entry, file);
    });

    const button = document.createElement('label');
    button.htmlFor = id;
    button.className = 'button button--secondary product-customizer__pick';
    button.textContent = photo?.status === 'ready' ? this.ui['change-photo'] : this.ui['choose-photo'];

    box.append(input, button);

    if (photo?.preview || photo?.url) {
      const image = document.createElement('img');
      image.className = 'product-customizer__preview';
      image.alt = '';
      image.src = photo.preview || photo.url;
      box.appendChild(image);
    }

    const status = document.createElement('p');
    status.className = 'product-customizer__status';
    status.setAttribute('aria-live', 'polite');
    if (photo?.status === 'uploading' || photo?.status === 'preparing') {
      box.classList.add('is-uploading');
      status.textContent = this.ui.uploading;
    }
    box.appendChild(status);

    if (photo?.status === 'failed' && photo.file) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--tertiary product-customizer__retry';
      retry.textContent = this.ui.retry;
      retry.addEventListener('click', () => this.uploadPhoto(entry, photo.file));
      box.appendChild(retry);
    }
    return box;
  }

  setValue(key, value) {
    const before = this.visibleEntries().map((entry) => entry.key).join('\u0000');
    this.values.set(key, value);
    const after = this.visibleEntries().map((entry) => entry.key).join('\u0000');
    if (before !== after) {
      this.render();
    } else {
      this.syncHiddenInputs();
      const wrapper = this.list.querySelector(`[data-key="${CSS.escape(key)}"]`);
      const entry = this.visibleEntries().find((candidate) => candidate.key === key);
      if (wrapper && entry) this.showError(wrapper, this.errorFor(entry));
    }
  }

  showError(wrapper, message) {
    if (!wrapper) return;
    const element = wrapper.querySelector('.product-customizer__message');
    if (element) element.textContent = message || '';
    wrapper.classList.toggle('is-invalid', Boolean(message));
    wrapper.querySelectorAll('[data-control]').forEach((control) => {
      if (message) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    });
  }

  focusNext(from) {
    // Un arrêt par champ : le bouton coché d'un groupe (ou le premier), sinon le premier contrôle.
    const stops = [...this.list.querySelectorAll('[data-key]')].map((wrapper) => {
      const radios = [...wrapper.querySelectorAll('input[type="radio"][data-control]')];
      if (radios.length > 0) return radios.find((radio) => radio.checked) || radios[0];
      return wrapper.querySelector('[data-control]');
    });
    const current = stops.findIndex((stop) => stop?.closest('[data-key]') === from.closest('[data-key]'));
    const next = stops.slice(current + 1).find(Boolean);
    if (next) {
      next.focus();
      return;
    }
    const form = this.closest('form');
    const submit = form?.querySelector('[data-ref="add-to-cart-button"]');
    if (form && submit && !submit.disabled) form.requestSubmit(submit);
  }

  /** Les seuls champs envoyés au panier : un champ caché par valeur visible et non vide. */
  syncHiddenInputs() {
    const inputs = [];
    for (const entry of this.visibleEntries()) {
      const value = this.valueOf(entry);
      if (value === '') continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${entry.key}]`;
      input.value = value;
      inputs.push(input);
    }
    if (inputs.length > 0 && this.handle) {
      // Clé privée (préfixe « _ ») : invisible pour le client, lue pour retrouver la fiche utilisée.
      const marker = document.createElement('input');
      marker.type = 'hidden';
      marker.name = 'properties[_gls_customization]';
      marker.value = this.handle;
      inputs.push(marker);
    }
    this.hiddenInputs.replaceChildren(...inputs);
  }

  // --- validation à l'ajout au panier -----------------------------------------

  onValidate(event) {
    const entries = this.visibleEntries();
    let first = null;
    for (const entry of entries) {
      this.touched.add(entry.key);
      const message = this.errorFor(entry);
      const wrapper = this.list.querySelector(`[data-key="${CSS.escape(entry.key)}"]`);
      this.showError(wrapper, message);
      if (message && !first) first = wrapper;
    }
    if (first || this.isBusy()) {
      event.preventDefault();
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        first.querySelector('[data-control]')?.focus({ preventScroll: true });
      }
      return;
    }
    this.syncHiddenInputs();
  }

  // --- photos -----------------------------------------------------------------

  async proxyCall(method, path, body) {
    const response = await fetch(`${this.proxy}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`proxy ${response.status}`);
    return response.json();
  }

  async uploadPhoto(entry, file) {
    const { key } = entry;
    const previous = this.photos.get(key);
    if (previous?.preview) URL.revokeObjectURL(previous.preview);
    this.photos.set(key, { status: 'preparing', file });
    this.touched.add(key);
    this.render();

    let blob;
    try {
      blob = await toJpeg(file);
    } catch (error) {
      this.photos.set(key, { status: 'failed', message: this.ui['unsupported-format'] });
      this.render();
      return;
    }
    if (blob.size > MAX_UPLOAD_BYTES) {
      this.photos.set(key, { status: 'failed', message: this.ui['too-large'] });
      this.render();
      return;
    }

    const preview = URL.createObjectURL(blob);
    this.photos.set(key, { status: 'uploading', file, preview });
    this.render();

    try {
      const staged = await this.proxyCall('POST', '/uploads', { byte_size: blob.size, content_type: 'image/jpeg' });
      const body = new FormData();
      for (const parameter of staged.parameters || []) body.append(parameter.name, parameter.value);
      // Le fichier en DERNIER : le stockage ignore tout champ qui arrive après lui.
      body.append('file', blob, 'photo.jpg');
      const sent = await fetch(staged.url, { method: 'POST', body });
      if (!sent.ok) throw new Error(`staging ${sent.status}`);

      let result = await this.proxyCall('POST', `/uploads/${encodeURIComponent(staged.id)}/complete`);
      for (let attempt = 0; result.status !== 'ready' && attempt < POLL_ATTEMPTS; attempt += 1) {
        if (result.status === 'failed') break;
        await wait(1000 + attempt * 250);
        result = await this.proxyCall('GET', `/uploads/${encodeURIComponent(staged.id)}`);
      }
      if (result.status !== 'ready' || !result.url) throw new Error(`upload ${result.status}`);
      if (this.photos.get(key)?.preview !== preview) return; // une autre photo a été choisie entre-temps
      this.photos.set(key, { status: 'ready', url: result.url, preview });
    } catch (error) {
      console.error('product-customizer: envoi de la photo', error);
      if (this.photos.get(key)?.preview !== preview) return;
      this.photos.set(key, { status: 'failed', file, preview, message: this.ui['upload-failed'] });
    }
    this.render();
  }
}

if (!customElements.get('product-customizer')) {
  customElements.define('product-customizer', ProductCustomizer);
}
